import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Button,

  Card,
  CardContent,
  Grid,
  Divider
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import AssessmentIcon from '@mui/icons-material/Assessment';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';

const StatisticsPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Simular carregamento
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleRefresh = () => {
    setLoading(true);
    // Recarregar o iframe
    const iframe = document.getElementById('cnj-statistics') as HTMLIFrameElement;
    if (iframe) {
      iframe.src = iframe.src;
    }
    setTimeout(() => setLoading(false), 2000);
  };

  const statisticsInfo = [
    {
      title: "Processos Pendentes",
      description: "Visualize o volume de processos aguardando julgamento em todos os tribunais"
    },
    {
      title: "Produtividade",
      description: "Acompanhe métricas de produtividade dos tribunais e magistrados"
    },
    {
      title: "Tempos Processuais",
      description: "Analise os tempos médios de tramitação por tribunal e instância"
    },
    {
      title: "Classes e Assuntos",
      description: "Explore distribuição por classes processuais e assuntos mais comuns"
    }
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ pt: 2, pb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AssessmentIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Estatísticas do Poder Judiciário
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Dados oficiais do CNJ - Conselho Nacional de Justiça
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Atualizar
            </Button>
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              href="https://justica-em-numeros.cnj.jus.br/painel-estatisticas/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Abrir Original
            </Button>
          </Box>
        </Box>

        {/* Informações sobre as estatísticas */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
            O que você pode encontrar neste painel
          </Typography>
          <Grid container spacing={2}>
            {statisticsInfo.map((info, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ 
                  height: '100%', 
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 1
                  }
                }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: 'primary.main' }}>
                      {info.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {info.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>



        {/* Painel embedado */}
        <Paper sx={{ position: 'relative', overflow: 'hidden' }}>
          {loading && (
            <Box 
              sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
                minHeight: '600px'
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Carregando painel de estatísticas...
                </Typography>
              </Box>
            </Box>
          )}

          {error ? (
            <Alert 
              severity="error" 
              sx={{ m: 2 }}
              action={
                <Button color="inherit" size="small" onClick={handleRefresh}>
                  Tentar Novamente
                </Button>
              }
            >
              {error}
            </Alert>
          ) : (
            <Box sx={{ width: '100%', height: '80vh', minHeight: '600px' }}>
              <iframe
                id="cnj-statistics"
                src="https://justica-em-numeros.cnj.jus.br/painel-estatisticas/"
                width="100%"
                height="100%"
                frameBorder="0"
                style={{ 
                  border: 'none',
                  borderRadius: '4px'
                }}
                title="Painel de Estatísticas do CNJ"
                onLoad={() => setLoading(false)}
                onError={() => {
                  setError('Erro ao carregar o painel. Verifique sua conexão ou tente novamente.');
                  setLoading(false);
                }}
              />
            </Box>
          )}
        </Paper>

        {/* Informações adicionais */}
        <Paper sx={{ p: 3, mt: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
            Sobre os Dados
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                Atualização
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Os dados são atualizados periodicamente pelo CNJ com base nas informações 
                fornecidas pelos tribunais de todo o país.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                Abrangência
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Inclui dados de todos os segmentos da Justiça: Estadual, Federal, 
                Trabalhista, Eleitoral e Militar.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                Filtros Disponíveis
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Utilize os filtros do painel para analisar dados específicos por tribunal, 
                grau, órgão julgador e outras dimensões.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                Uso Profissional
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Ferramenta valiosa para análises estratégicas, planejamento de atuação 
                e compreensão do cenário judicial.
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default StatisticsPage; 